import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { FaCoins, FaChartLine, FaSync, FaCheckCircle, FaClock, FaExclamationTriangle, FaFilePdf } from 'react-icons/fa';
import { useAuth0 } from '@auth0/auth0-react';
import html2pdf from 'html2pdf.js';
import BlurredComponent from './BlurredComponent';
import { useToast } from '../../../Context/ToastContext';

import { 
    COLORS, 
    PanelContent, 
    PanelTitle,
    StatGrid,
    StatCard,
    ChartContainer
} from './styles';

// Mock data for Earnings Summary
const mockEarningsData = {
    thisMonth: 2450,
    allTime: 18750,
    pendingPayments: 750,
    nextPayout: "June 15, 2024",
    monthlyComparison: 18, // percentage increase from previous month
    monthlyEarnings: [
        { name: 'Jan', amount: 1800, paidEarnings: 1800, pendingPayments: 120 },
        { name: 'Feb', amount: 1950, paidEarnings: 1950, pendingPayments: 180 },
        { name: 'Mar', amount: 1700, paidEarnings: 1700, pendingPayments: 90 },
        { name: 'Apr', amount: 2100, paidEarnings: 2100, pendingPayments: 250 },
        { name: 'May', amount: 2450, paidEarnings: 2450, pendingPayments: 350 }
    ]
};

/**
 * EarningsSummary Component
 * 
 * Displays earnings data and visualizations
 */
const EarningsSummary = () => {
    const { user, getAccessTokenSilently } = useAuth0();
    const { showToast } = useToast();
    const [earningsData, setEarningsData] = useState(mockEarningsData);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [hasRapydWallet, setHasRapydWallet] = useState(false);
    const [walletStatus, setWalletStatus] = useState('unknown');

    // Check if Rapyd is enabled
    const isRapydEnabled = process.env.REACT_APP_ENABLE_RAPYD === 'true';

    // Fetch real Rapyd earnings data
    const fetchEarningsData = async () => {
        if (!user?.sub || !isRapydEnabled) return;

        setIsLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const response = await fetch(`${process.env.REACT_APP_SERVER}payment/expert/${user.sub}/earnings`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    const rapydData = result.data;

                    const mappedData = {
                        thisMonth: rapydData.thisMonth || 0,
                        allTime: rapydData.allTime || 0,
                        pendingPayments: rapydData.pendingPayments || 0,
                        nextPayout: rapydData.nextPayout || "Not scheduled",
                        monthlyComparison: rapydData.monthlyComparison || 0,
                        monthlyEarnings: rapydData.monthlyEarnings || mockEarningsData.monthlyEarnings,
                        walletBalance: rapydData.walletBalance,
                        paymentRequestsCount: rapydData.paymentRequestsCount
                    };

                    setEarningsData(mappedData);
                    setHasRapydWallet(rapydData.hasWallet);
                    setWalletStatus(rapydData.hasWallet ? 'connected' : 'not_connected');
                }
            }
        } catch (error) {
            console.error('Error fetching earnings data:', error);
            setWalletStatus('error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user?.sub && isRapydEnabled) {
            fetchEarningsData();
        }
    }, [user, isRapydEnabled]);

    // Generate PDF report
    const generatePdfReport = async () => {
        setIsDownloading(true);
        try {
            const reportContent = `
                <div style="font-family: Arial, sans-serif; padding: 30px; color: #333;">
                    <div style="text-align: center; margin-bottom: 30px;">
                        <h1 style="color: #2196F3; margin: 0;">Expert Earnings Report</h1>
                        <p style="margin: 10px 0 0 0; color: #666;">Generated on ${new Date().toLocaleDateString()}</p>
                    </div>

                    <div style="background: #f8f9fa; padding: 20px; margin-bottom: 30px; border-left: 4px solid #2196F3;">
                        <h3 style="margin-top: 0; color: #2196F3;">Report Information</h3>
                        <p><strong>Expert:</strong> ${user?.name || 'Unknown Expert'}</p>
                        <p><strong>Data Source:</strong> ${isRapydEnabled && hasRapydWallet ? 'Live Rapyd Data' : 'Mock Data'}</p>
                        <p><strong>Report Date:</strong> ${new Date().toLocaleDateString()}</p>
                    </div>

                    <div style="display: flex; flex-wrap: wrap; gap: 20px; margin-bottom: 30px;">
                        <div style="background: #fff; border: 1px solid #e0e0e0; padding: 20px; text-align: center; flex: 1; min-width: 200px;">
                            <h4 style="margin: 0 0 10px 0; color: #666; text-transform: uppercase;">This Month</h4>
                            <div style="font-size: 24px; font-weight: bold; color: #2196F3;">$${earningsData.thisMonth.toLocaleString()}</div>
                            <div style="font-size: 12px; color: #4CAF50;">${earningsData.monthlyComparison >= 0 ? '+' : ''}${earningsData.monthlyComparison}%</div>
                        </div>
                        <div style="background: #fff; border: 1px solid #e0e0e0; padding: 20px; text-align: center; flex: 1; min-width: 200px;">
                            <h4 style="margin: 0 0 10px 0; color: #666; text-transform: uppercase;">All Time</h4>
                            <div style="font-size: 24px; font-weight: bold; color: #2196F3;">$${earningsData.allTime.toLocaleString()}</div>
                        </div>
                        <div style="background: #fff; border: 1px solid #e0e0e0; padding: 20px; text-align: center; flex: 1; min-width: 200px;">
                            <h4 style="margin: 0 0 10px 0; color: #666; text-transform: uppercase;">Pending</h4>
                            <div style="font-size: 24px; font-weight: bold; color: #2196F3;">$${earningsData.pendingPayments.toLocaleString()}</div>
                        </div>
                    </div>

                    <div style="margin-bottom: 30px;">
                        <h3 style="color: #333; border-bottom: 2px solid #2196F3; padding-bottom: 10px;">Monthly Breakdown</h3>
                        <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
                            <thead>
                                <tr>
                                    <th style="padding: 12px; text-align: left; background-color: #2196F3; color: white;">Month</th>
                                    <th style="padding: 12px; text-align: left; background-color: #2196F3; color: white;">Earnings</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${earningsData.monthlyEarnings.map((month, index) => `
                                    <tr style="${index % 2 === 1 ? 'background-color: #f8f9fa;' : ''}">
                                        <td style="padding: 12px; border-bottom: 1px solid #ddd;">${month.name}</td>
                                        <td style="padding: 12px; border-bottom: 1px solid #ddd;">$${month.amount.toLocaleString()}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>

                    <div style="margin-top: 40px; text-align: center; color: #666; font-size: 12px; border-top: 1px solid #ddd; padding-top: 20px;">
                        <p><strong>Imaven Platform</strong> - Expert Earnings Report</p>
                        <p>This report contains confidential financial information.</p>
                    </div>
                </div>
            `;

            await html2pdf().from(reportContent).set({
                margin: 0.5,
                filename: `earnings-report-${new Date().toISOString().split('T')[0]}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2 },
                jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
            }).save();

        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('Failed to generate PDF. Please try again.', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <PanelContent>
            {isRapydEnabled ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <PanelTitle><FaCoins /> Earnings Summary</PanelTitle>
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '11px', color: COLORS.muted }}>
                            {isLoading && <FaSync className="fa-spin" style={{ marginRight: '5px' }} />}
                            {walletStatus === 'connected' && <FaCheckCircle style={{ color: '#4CAF50', marginRight: '5px' }} />}
                            {walletStatus === 'not_connected' && <FaExclamationTriangle style={{ color: '#FF9800', marginRight: '5px' }} />}
                            {walletStatus === 'error' && <FaExclamationTriangle style={{ color: '#f44336', marginRight: '5px' }} />}
                            {walletStatus === 'connected' ? 'Live Rapyd Data' : 'Mock Data'}
                        </div>
                    </div>

            <StatGrid>
                <StatCard $large>
                    <div className="stat-title">This Month</div>
                    <div className="stat-value">${earningsData.thisMonth}</div>
                    <div className="stat-footer" $positive={earningsData.monthlyComparison >= 0}>
                        <FaChartLine style={{ fontSize: window.innerWidth <= 1920 ? '10px' : '12px' }} />
                        {earningsData.monthlyComparison >= 0 ? '+' : ''}{earningsData.monthlyComparison}% from last month
                    </div>
                </StatCard>

                <StatCard>
                    <div className="stat-title">All Time</div>
                    <div className="stat-value">${earningsData.allTime}</div>
                    {isRapydEnabled && hasRapydWallet && earningsData.walletBalance !== null && (
                        <div className="stat-footer" style={{ fontSize: window.innerWidth <= 1920 ? '9px' : '11px' }}>
                            Wallet Balance: ${earningsData.walletBalance.toFixed(2)}
                        </div>
                    )}
                </StatCard>
            </StatGrid>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: window.innerWidth <= 1920 ? '4px' : '8px', gap: '8px' }}>
                <StatCard style={{ width: '48%' }}>
                    <div className="stat-title">Pending Payments</div>
                    <div className="stat-value">${earningsData.pendingPayments}</div>
                    {isRapydEnabled && hasRapydWallet && earningsData.paymentRequestsCount && (
                        <div className="stat-footer" style={{ fontSize: window.innerWidth <= 1920 ? '9px' : '11px' }}>
                            {earningsData.paymentRequestsCount.pending} pending of {earningsData.paymentRequestsCount.total} total
                        </div>
                    )}
                </StatCard>

                <StatCard style={{ width: '48%' }}>
                    <div className="stat-title">Next Payout</div>
                    <div className="stat-value" style={{ fontSize: window.innerWidth <= 1920 ? '13px' : '16px' }}>{earningsData.nextPayout}</div>
                </StatCard>
            </div>
            
            {/* Chart section */}
            <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                marginTop: window.innerWidth <= 1920 ? '8px' : '12px',
                position: 'relative',
                minHeight: 0
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: window.innerWidth <= 1920 ? '6px' : '10px'
                }}>
                    <div style={{
                        color: COLORS.light,
                        fontSize: window.innerWidth <= 1920 ? '11px' : '14px'
                    }}>Monthly Earnings</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isRapydEnabled && (
                            <button
                                onClick={fetchEarningsData}
                                disabled={isLoading}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${COLORS.muted}`,
                                    color: isLoading ? COLORS.muted : COLORS.light,
                                    padding: window.innerWidth <= 1920 ? '2px 6px' : '4px 8px',
                                    paddingTop: window.innerWidth <= 1920 ? '4px' : '6px',
                                    borderRadius: '3px',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    fontSize: window.innerWidth <= 1920 ? '9px' : '11px'
                                }}
                            >
                                <FaSync className={isLoading ? 'fa-spin' : ''} style={{ marginRight: '3px' }} />
                                Refresh
                            </button>
                        )}
                        <button
                            onClick={generatePdfReport}
                            disabled={isDownloading}
                            style={{
                                backgroundColor: isDownloading ? 'transparent' : COLORS.primary,
                                border: `1px solid ${COLORS.primary}`,
                                color: isDownloading ? COLORS.muted : '#fff',
                                padding: window.innerWidth <= 1920 ? '2px 6px' : '4px 8px',
                                paddingTop: window.innerWidth <= 1920 ? '4px' : '6px',
                                borderRadius: '3px',
                                cursor: isDownloading ? 'not-allowed' : 'pointer',
                                fontSize: window.innerWidth <= 1920 ? '9px' : '11px',
                                fontWeight: 'bold'
                            }}
                        >
                            {isDownloading ? (
                                <>
                                    <FaSync className="fa-spin" style={{ marginRight: '3px' }} />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <FaFilePdf style={{ marginRight: '3px' }} />
                                    PDF Report
                                </>
                            )}
                        </button>
                    </div>
                </div>
                
                <ChartContainer $height={window.innerWidth <= 1920 ? '100px' : '160px'} $marginTop="0">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={earningsData.monthlyEarnings}
                            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                            <XAxis dataKey="name" stroke={COLORS.muted} fontSize={window.innerWidth <= 1920 ? 9 : 11} />
                            <YAxis stroke={COLORS.muted} fontSize={window.innerWidth <= 1920 ? 9 : 11} />
                            <RechartsTooltip
                                formatter={(value, name) => {
                                    const label = name === 'paidEarnings' ?
                                        (isRapydEnabled && hasRapydWallet ? 'Real Earnings' : 'Mock Earnings') :
                                        'Pending Payments';
                                    return [`$${value}`, label];
                                }}
                                contentStyle={{ 
                                    backgroundColor: COLORS.bgTertiary, 
                                    borderColor: COLORS.border,
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                                }}
                                labelStyle={{ color: COLORS.light }}
                            />
                            <Bar
                                dataKey="paidEarnings"
                                fill={isRapydEnabled && hasRapydWallet ? COLORS.primary : COLORS.muted}
                                radius={[4, 4, 0, 0]}
                                name="paidEarnings"
                            />
                            <Bar
                                dataKey="pendingPayments"
                                fill={isRapydEnabled && hasRapydWallet ? '#FF9800' : '#666'}
                                radius={[4, 4, 0, 0]}
                                name="pendingPayments"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            </div>
                </>
            ) : (
                <BlurredComponent>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <PanelTitle><FaCoins /> Earnings Summary</PanelTitle>
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '11px', color: COLORS.muted }}>
                            Mock Data
                        </div>
                    </div>

                    <StatGrid>
                        <StatCard $large>
                            <div className="stat-title">This Month</div>
                            <div className="stat-value">${earningsData.thisMonth}</div>
                            <div className="stat-footer" $positive={earningsData.monthlyComparison >= 0}>
                                <FaChartLine style={{ fontSize: window.innerWidth <= 1920 ? '10px' : '12px' }} />
                                {earningsData.monthlyComparison >= 0 ? '+' : ''}{earningsData.monthlyComparison}% from last month
                            </div>
                        </StatCard>

                        <StatCard>
                            <div className="stat-title">All Time</div>
                            <div className="stat-value">${earningsData.allTime}</div>
                        </StatCard>
                    </StatGrid>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: window.innerWidth <= 1920 ? '4px' : '8px', gap: '8px' }}>
                        <StatCard style={{ width: '48%' }}>
                            <div className="stat-title">Pending Payments</div>
                            <div className="stat-value">${earningsData.pendingPayments}</div>
                        </StatCard>

                        <StatCard style={{ width: '48%' }}>
                            <div className="stat-title">Next Payout</div>
                            <div className="stat-value" style={{ fontSize: window.innerWidth <= 1920 ? '13px' : '16px' }}>{earningsData.nextPayout}</div>
                        </StatCard>
                    </div>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        marginTop: window.innerWidth <= 1920 ? '8px' : '12px',
                        position: 'relative',
                        minHeight: 0
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: window.innerWidth <= 1920 ? '6px' : '10px'
                        }}>
                            <div style={{
                                color: COLORS.light,
                                fontSize: window.innerWidth <= 1920 ? '11px' : '14px'
                            }}>Monthly Earnings</div>
                        </div>

                        <ChartContainer $height={window.innerWidth <= 1920 ? '100px' : '160px'} $marginTop="0">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={earningsData.monthlyEarnings}
                                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                                    <XAxis dataKey="name" stroke={COLORS.muted} fontSize={window.innerWidth <= 1920 ? 9 : 11} />
                                    <YAxis stroke={COLORS.muted} fontSize={window.innerWidth <= 1920 ? 9 : 11} />
                                    <RechartsTooltip
                                        formatter={(value, name) => {
                                            const label = name === 'paidEarnings' ? 'Mock Earnings' : 'Pending Payments';
                                            return [`$${value}`, label];
                                        }}
                                        contentStyle={{ 
                                            backgroundColor: COLORS.bgTertiary, 
                                            borderColor: COLORS.border,
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)'
                                        }}
                                        labelStyle={{ color: COLORS.light }}
                                    />
                                    <Bar
                                        dataKey="paidEarnings"
                                        fill={COLORS.muted}
                                        radius={[4, 4, 0, 0]}
                                        name="paidEarnings"
                                    />
                                    <Bar
                                        dataKey="pendingPayments"
                                        fill="#666"
                                        radius={[4, 4, 0, 0]}
                                        name="pendingPayments"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </div>
                </BlurredComponent>
            )}
        </PanelContent>
    );
};

export default EarningsSummary;
